import { Typography } from '@up-riac/bespoke-ui';
import React, { Suspense } from 'react';
import { HeaderMobileMenuHeader } from 'src/components/Header/components/HeaderMobileMenu/HeaderMobileMenuHeader';
import { HeaderMobileMenuNavItem } from 'src/components/Header/components/HeaderMobileMenu/HeaderMobileMenuNavItem';
import { usePrefetchSuspense } from 'src/lib/usePrefetch';

const HeaderMobileMenuLatestPageSubpages = ({ localPage, setLocalPage, data, closeMenu }) => {
  return (
    <>
      {localPage === 'news' && <>
        <HeaderMobileMenuHeader
          id='header-mobile-menu-latest-news-header'
          onBack={() => setLocalPage('main')}
          onPress={closeMenu}
          to='/news-and-events#news'
        >
          News
        </HeaderMobileMenuHeader>
        {data.read().news.news.map((item, index) => (
          <HeaderMobileMenuNavItem
            key={`header-mobile-menu-latest-news-item-${index}`}
            id={`header-mobile-menu-latest-news-item-${index}`}
            label={item.title}
            description={item.content}
            to={item.navigation}
            onPress={() => closeMenu()}
            noArrow
          />
        ))}
        <HeaderMobileMenuNavItem
          id='header-mobile-menu-latest-news-view-all-item'
          label='View All Posts'
          to='/news-and-events#news'
          noArrow
          onPress={() => closeMenu()}
        />
      </>}
      {localPage === 'blog' && <>
        <HeaderMobileMenuHeader
          id='header-mobile-menu-latest-blog-header'
          onBack={() => setLocalPage('main')}
          onPress={closeMenu}
          to='/news-and-events#blogs'
        >
          Blogs
        </HeaderMobileMenuHeader>
        {data.read().blog.news.map((item, index) => (
          <HeaderMobileMenuNavItem
            key={`header-mobile-menu-latest-blog-item-${index}`}
            id={`header-mobile-menu-latest-blog-item-${index}`}
            label={item.title}
            description={item.content}
            to={item.navigation}
            onPress={() => closeMenu()}
            noArrow
          />
        ))}
        <HeaderMobileMenuNavItem
          id='header-mobile-menu-latest-blog-view-all-item'
          label='View All Posts'
          to='/news-and-events#blogs'
          onPress={() => closeMenu()}
          noArrow
        />
      </>}
      {localPage === 'videos' && <>
        <HeaderMobileMenuHeader
          id='header-mobile-menu-latest-videos-header'
          onBack={() => setLocalPage('main')}
          to='/news-and-events#videos'
          onPress={() => closeMenu()}
        >
          Videos
        </HeaderMobileMenuHeader>
        {data.read().videos.news.map((item, index) => (
          <HeaderMobileMenuNavItem
            key={`header-mobile-menu-latest-videos-item-${index}`}
            id={`header-mobile-menu-latest-videos-item-${index}`}
            label={item.title}
            description={item.content}
            to={item.navigation}
            onPress={() => closeMenu()}
            noArrow
          />
        ))}
        <HeaderMobileMenuNavItem
          id='header-mobile-menu-latest-videos-view-all-item'
          label='View All Posts'
          to='/news-and-events#videos'
          noArrow
          onPress={() => closeMenu()}
        />
      </>}
      {localPage === 'events' && <>
        <HeaderMobileMenuHeader
          id='header-mobile-menu-latest-events-header'
          onBack={() => setLocalPage('main')}
          to='/news-and-events#events'
          onPress={() => closeMenu()}
        >
          Events
        </HeaderMobileMenuHeader>
        {data.read().events.news.map((item, index) => (
          <HeaderMobileMenuNavItem
            key={`header-mobile-menu-latest-events-item-${index}`}
            id={`header-mobile-menu-latest-events-item-${index}`}
            label={item.title}
            description={item.content}
            to={item.navigation}
            onPress={() => closeMenu()}
            noArrow
          />
        ))}
        <HeaderMobileMenuNavItem
          id='header-mobile-menu-latest-events-view-all-item'
          label='View All Posts'
          to='/news-and-events#events'
          noArrow
          onPress={() => closeMenu()}
        />
      </>}
    </>
  );
};

export const HeaderMobileMenuLatestPage = ({ setPage, closeMenu }: {closeMenu: () => void, setPage: React.Dispatch<React.SetStateAction<'main' | 'login' | 'auctions' | 'consignment' | 'latest' | 'company' | 'locations' | 'account' | 'account/profile' | 'account/settings' | 'account/buying'>>}) => {
  const [localPage, setLocalPage] = React.useState<'main' | 'news' | 'blog' | 'videos' | 'events'>('main');

  const fetchPage = async (fetch, start: number, count: number, type: string, query?: string) => {
    // TODO: These queries could be done all at once instead of one at a time.
    // News query
    const newsReq = query ? await fetch(`/api/ghost/getPostsBySearch/${start}/${count + 1}/${type}/${encodeURIComponent(query)}`): await fetch(`/api/ghost/getPostsByPage/${start}/${count + 1}/${type}`);
    const newsRes = await newsReq.json();
    const news = [];
    let hasNextPage = false;
    if(newsRes.length === count + 1) {
      hasNextPage = true;
      newsRes.pop();
    }
    await newsRes.map((item) => {
      return news.push({
        title: item.title,
        image: item.image,
        date: item.published_at,
        content: `${item.custom_excerpt || item.excerpt}...`,
        navigation: item.tags.includes('events') ? `/events/${item.slug}` : item.tags.includes('news') ? `/news/${item.slug}` : item.tags.includes('blogs') ? `/riac-blog/${item.slug}` : item.tags.includes('videos') ? `/video/${item.slug}` : null,
        tags: item.tags,
      });
    });
    return { news, hasNextPage };
  };
  const data = usePrefetchSuspense('header-mobile-menu-latest-page', async () => {
    const news = await fetchPage(fetch, 0, 3, 'news');
    const blog = await fetchPage(fetch, 0, 3, 'blogs');
    const videos = await fetchPage(fetch, 0, 3, 'videos');
    const events = await fetchPage(fetch, 0, 3, 'events');
    return { news, blog, videos, events };
  });
  return (<>
    {localPage === 'main' && <>
      <HeaderMobileMenuHeader
        id='header-mobile-menu-latest-page-header'
        onBack={() => setPage('main')}
        onPress={closeMenu}
        to='/news-and-events'
      >
        Latest
      </HeaderMobileMenuHeader>
      <HeaderMobileMenuNavItem id='header-mobile-menu-latest-news-item' label='News' onPress={() => setLocalPage('news')}/>
      <HeaderMobileMenuNavItem id='header-mobile-menu-latest-blog-item' label='Blogs' onPress={() => setLocalPage('blog')}/>
      <HeaderMobileMenuNavItem id='header-mobile-menu-latest-videos-item' label='Videos' onPress={() => setLocalPage('videos')}/>
      <HeaderMobileMenuNavItem id='header-mobile-menu-latest-events-item' label='Events' onPress={() => setLocalPage('events')}/>
    </>}
    <Suspense fallback={<Typography color='text-light'>Loading...</Typography>}>
      <HeaderMobileMenuLatestPageSubpages closeMenu={closeMenu} data={data} localPage={localPage} setLocalPage={setLocalPage} />
    </Suspense>
  </>);
};
